import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING } from '../constants/theme';

const FamilyLinkRequestModal = ({ onClose, onRequestSent }) => {
  const { profile } = useAuth();
  const { isDesktop } = useResponsive();
  const [playerEmail, setPlayerEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationResult, setValidationResult] = useState(null);

  const ensureParentHasFamilyId = async () => {
    // If parent doesn't have a family_id, generate one
    if (!profile.family_id) {
      const newFamilyId = crypto.randomUUID();
      const { error } = await supabase
        .from('profiles')
        .update({ family_id: newFamilyId })
        .eq('id', profile.id);

      if (error) throw error;

      // Update local profile (in production, you'd refresh AuthContext)
      profile.family_id = newFamilyId;
      return newFamilyId;
    }
    return profile.family_id;
  };

  const handleValidateEmail = async () => {
    if (!playerEmail.trim()) {
      setError('Please enter a player email address');
      return;
    }

    setError('');
    setValidating(true);
    setValidationResult(null);

    try {
      const { data, error } = await supabase.rpc('can_player_be_linked', {
        player_email: playerEmail.trim(),
      });

      if (error) throw error;

      const result = data[0];
      if (result.can_link) {
        setValidationResult({
          valid: true,
          playerId: result.player_id,
          playerName: result.player_name,
        });
        setError('');
      } else {
        setError(result.error_message);
        setValidationResult(null);
      }
    } catch (err) {
      console.error('Error validating player:', err);
      setError(err.message);
      setValidationResult(null);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validationResult || !validationResult.valid) {
      setError('Please validate the player email first');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Ensure parent has family_id
      const familyId = await ensureParentHasFamilyId();

      // Calculate expiration date (7 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Create request
      const { error } = await supabase.from('family_link_requests').insert({
        parent_id: profile.id,
        player_id: validationResult.playerId,
        organization_id: profile.organization_id,
        parent_family_id: familyId,
        parent_message: message.trim() || null,
        expires_at: expiresAt.toISOString(),
      });

      if (error) throw error;

      setSuccess(`Family link request sent to ${validationResult.playerName}!`);

      // Notify parent component
      if (onRequestSent) {
        onRequestSent();
      }

      // Close modal after brief delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error sending family link request:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isDesktop ? SPACING.xl : 0,
    },
    modal: isDesktop
      ? {
          backgroundColor: COLORS.white,
          borderRadius: '8px',
          padding: SPACING.xl,
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }
      : {
          backgroundColor: COLORS.white,
          borderRadius: '8px 8px 0 0',
          padding: SPACING.lg,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
        },
    title: {
      marginTop: 0,
      marginBottom: SPACING.lg,
      fontSize: isDesktop ? '24px' : '20px',
      color: COLORS.textDark,
    },
    description: {
      marginBottom: SPACING.lg,
      fontSize: '14px',
      color: COLORS.textMuted,
      lineHeight: '1.5',
    },
    formGroup: {
      marginBottom: SPACING.md,
    },
    label: {
      display: 'block',
      marginBottom: SPACING.xs,
      fontWeight: '600',
      fontSize: '16px',
      color: COLORS.textDark,
    },
    input: {
      width: '100%',
      minHeight: '44px',
      padding: SPACING.sm,
      fontSize: '16px',
      border: `1px solid ${COLORS.border}`,
      borderRadius: '4px',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      minHeight: '100px',
      padding: SPACING.sm,
      fontSize: '16px',
      border: `1px solid ${COLORS.border}`,
      borderRadius: '4px',
      fontFamily: 'inherit',
      resize: 'vertical',
      boxSizing: 'border-box',
    },
    buttonGroup: {
      display: 'flex',
      gap: SPACING.sm,
      marginTop: SPACING.lg,
      flexDirection: isDesktop ? 'row' : 'column',
    },
    button: {
      minHeight: '44px',
      padding: `${SPACING.sm} ${SPACING.md}`,
      backgroundColor: COLORS.primary,
      color: COLORS.white,
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
      fontWeight: '500',
      flex: 1,
    },
    secondaryButton: {
      minHeight: '44px',
      padding: `${SPACING.sm} ${SPACING.md}`,
      backgroundColor: COLORS.white,
      color: COLORS.primary,
      border: `2px solid ${COLORS.primary}`,
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
      fontWeight: '500',
      flex: 1,
    },
    validateButton: {
      minHeight: '44px',
      padding: `${SPACING.sm} ${SPACING.md}`,
      backgroundColor: '#6c757d',
      color: COLORS.white,
      border: 'none',
      borderRadius: '4px',
      fontSize: '14px',
      cursor: 'pointer',
      fontWeight: '500',
      marginTop: SPACING.xs,
    },
    errorMessage: {
      padding: SPACING.md,
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      marginBottom: SPACING.md,
      fontSize: '14px',
    },
    successMessage: {
      padding: SPACING.md,
      backgroundColor: '#efe',
      color: '#3c3',
      borderRadius: '4px',
      marginBottom: SPACING.md,
      fontSize: '14px',
    },
    validationSuccess: {
      padding: SPACING.md,
      backgroundColor: '#e8f5e9',
      color: '#388e3c',
      borderRadius: '4px',
      marginTop: SPACING.sm,
      fontSize: '14px',
      fontWeight: '500',
    },
    helpText: {
      fontSize: '13px',
      color: COLORS.textMuted,
      marginTop: SPACING.xs,
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={styles.title}>Link Existing Player</h2>
        <p style={styles.description}>
          Send a family link request to an existing player. They'll be able to approve or decline
          your request from their dashboard.
        </p>

        {error && <div style={styles.errorMessage}>{error}</div>}
        {success && <div style={styles.successMessage}>{success}</div>}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Player Email Address <span style={{ color: '#d32f2f' }}>*</span>
            </label>
            <input
              type="email"
              style={styles.input}
              value={playerEmail}
              onChange={(e) => {
                setPlayerEmail(e.target.value);
                setValidationResult(null);
              }}
              placeholder="player@example.com"
              required
              disabled={loading}
            />
            <p style={styles.helpText}>
              Enter the email address of the player you want to link to your family
            </p>
            <button
              type="button"
              style={{
                ...styles.validateButton,
                opacity: validating || loading ? 0.6 : 1,
              }}
              onClick={handleValidateEmail}
              disabled={validating || loading || !playerEmail.trim()}
            >
              {validating ? 'Validating...' : 'Validate Email'}
            </button>
            {validationResult && validationResult.valid && (
              <div style={styles.validationSuccess}>
                ✓ Player found: {validationResult.playerName}
              </div>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Message (Optional)</label>
            <textarea
              style={styles.textarea}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal message to your request..."
              disabled={loading}
            />
            <p style={styles.helpText}>
              This message will be visible to the player when they review your request
            </p>
          </div>

          <div style={styles.buttonGroup}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.button,
                opacity: loading || !validationResult ? 0.6 : 1,
              }}
              disabled={loading || !validationResult || !validationResult.valid}
            >
              {loading ? 'Sending Request...' : 'Send Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FamilyLinkRequestModal;
